import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BodyText } from '@/common/components/BodyText/BodyText';
import { Heading } from '@/common/components/Heading/Heading';
import { Icon } from '@/common/components/Icons/Icon';
import { LabeledValueList } from '@/common/components/LabeledValueList/LabeledValueList';
import { OperationSummaryDetail } from '@/common/components/OperationDetail/components/OperationSummaryDetail/OperationSummaryDetail';
import { Stack } from '@/common/components/Stack/Stack';
import { Translate } from '@/common/components/Translate/Translate';
import { useGetBenefitDetail } from '@/common/integrations/menhir/employee/benefits/benefits.hooks';
import { IOperation } from '@/common/integrations/menhir/employee/operations/operations.types';
import { formatDate, formatTime } from '@/common/utils/date';
import { MoneyUtils, TMoney } from '@coverflex-tech/hypernova-utils';
import { OperationsLearningCenter } from '../OperationsLearningCenter/OperationsLearningCenter';
import { FailedAction } from './components/FailedAction/FailedAction';

export type TFailedDetailDrawerProps = {
  operation: IOperation;
};

export const FailedDetailDrawer = ({ operation }: TFailedDetailDrawerProps) => {
  const { t } = useTranslation();
  const [learningCentreOpen, setLearningCentreOpen] = useState(false);

  const isBenefit =
    operation.categorySlug === 'fringe' || operation.categorySlug === 'meals';

  const { benefit } = useGetBenefitDetail(
    {
      slug: operation.categorySlug!,
    },
    { enabled: isBenefit }
  );

  const rejectionReason = operation.descriptionParams.find(
    (param) => param.key === 'rejection_reason'
  )?.value;

  const voucherValue = operation.descriptionParams.find(
    (param) => param.key === 'voucher_value'
  )?.value as TMoney;

  const remainingValue = operation.descriptionParams.find(
    (param) => param.key === 'remaining_value'
  )?.value as TMoney;

  const voucherCount = operation.descriptionParams.find(
    (param) => param.key === 'voucher_count'
  )?.value as number;

  return (
    <>
      <Stack justify="between" grow>
        <Stack gap="xl">
          <OperationSummaryDetail
            icon={<Icon icon="ban" color="systemError" />}
            operation={operation}
          />
          <LabeledValueList.Root title="">
            <LabeledValueList.Item
              label={t('general.labels.date_and_time')}
              value={t('general.labels.date_and_time.value', {
                date: formatDate(operation.executedAt),
                time: formatTime(operation.executedAt),
              })}
            />
            {voucherCount && (
              <LabeledValueList.Item
                label={t('operations.payable_with_credits.label')}
                value={t('operations.payable_with_credits.value', {
                  voucherCount,
                  voucherValue: MoneyUtils.moneyToString(voucherValue),
                })}
              />
            )}

            {remainingValue && (
              <LabeledValueList.Item
                label={t('operations.remaining_value.label')}
                value={MoneyUtils.moneyToString(remainingValue)}
              />
            )}
          </LabeledValueList.Root>
          {rejectionReason && (
            <Translate
              i18nKey={`rejection_reason.${rejectionReason}`}
              values={{
                voucherValue:
                  isBenefit && MoneyUtils.moneyToString(voucherValue),
                fringeLimit:
                  isBenefit && MoneyUtils.moneyToString(benefit?.usage?.limit),
                merchantName:
                  'merchantName' in operation && operation.merchantName,
              }}
              parent={BodyText}
              components={{
                heading: <Heading level={3}>-</Heading>,
              }}
            />
          )}
        </Stack>
        {rejectionReason && (
          <FailedAction
            errorTag={rejectionReason as string}
            onOpenLearningCentre={() => setLearningCentreOpen(true)}
          />
        )}
      </Stack>
      {learningCentreOpen && (
        <OperationsLearningCenter
          operation={operation}
          onClose={() => setLearningCentreOpen(false)}
        />
      )}
    </>
  );
};
